/**
 * A function to create the mutations to set the history ids as a global app setting
 * in the store and to load it from the local storage.
 * 
 * @param {Object} [storage] generated with the `createNamespacedLocalStorageFunctions`
 *  function present in the `local-storage` file in the store directory. Is an optional
 *  parameter to store the value of this mutation in the local storage with the namespace
 *  of the correspondent module of the store.
 * 
 * @returns {Object} with the mutation to set the value in the store and another mutation
 *  to load the value from the local storage.
 */
export default storage => {
  const storageKey = 'appSettingsHistoryIds';

  /**
   * Set the `historyIds` app setting.
   * Very useful to filter the data of the app to look only for this items.
   *
   * @param {Object} state
   * @param {String[]} historyIds 
   */
  function setAppSettingsHistoryIds(state, historyIds) {
    if (!Array.isArray(historyIds)) {
      throw new Error('Please provide an array of historyIds (could be empty).')
    }

    if (historyIds.length && historyIds.some(id => typeof id !== 'string')) {
      throw new Error('Please provide only strings in the "historyIds" array');
    }

    const uniqueIds = [...new Set(historyIds)];
    state.appSettings.historyIds = uniqueIds;

    if (storage) {
      storage.setItem(storageKey, uniqueIds)
    }
  }

  /**
   * A mutation to load the setting from the local storage.
   * @param {Object} state
   */
  function loadAppSettingsHistoryIds(state) {
    const ids = storage.getItem(storageKey);

    if (ids) {
      setAppSettingsHistoryIds(state, ids.split(','))
    }
  }

  return { loadAppSettingsHistoryIds, setAppSettingsHistoryIds }
}
